import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const AdminPackages = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    followersCount: 250,
    platforms: [],
    price: 0,
    originalPrice: 0,
    description: '',
    features: [],
    deliveryTime: '',
    guarantee: '30 days',
    isActive: true,
    isPopular: false,
    discountPercentage: 0,
    category: 'standard',
    sortOrder: 0
  });

  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: packages, isLoading } = useQuery(
    'adminPackages',
    async () => {
      const response = await user?.api?.get('/packages');
      return response.data.data.packages;
    }
  );

  const createPackageMutation = useMutation(
    async (packageData) => {
      const response = await user?.api?.post('/admin/packages', packageData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminPackages');
        setShowCreateForm(false);
        setFormData({
          name: '',
          followersCount: 250,
          platforms: [],
          price: 0,
          originalPrice: 0,
          description: '',
          features: [],
          deliveryTime: '',
          guarantee: '30 days',
          isActive: true,
          isPopular: false,
          discountPercentage: 0,
          category: 'standard',
          sortOrder: 0
        });
        toast.success('Package created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create package');
      }
    }
  );

  const updatePackageMutation = useMutation(
    async ({ id, packageData }) => {
      const response = await user?.api?.put(`/admin/packages/${id}`, packageData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminPackages');
        setEditingPackage(null);
        toast.success('Package updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update package');
      }
    }
  );

  const deletePackageMutation = useMutation(
    async (packageId) => {
      const response = await user?.api?.delete(`/admin/packages/${packageId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminPackages');
        toast.success('Package deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete package');
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingPackage) {
      updatePackageMutation.mutate({ id: editingPackage._id, packageData: formData });
    } else {
      createPackageMutation.mutate(formData);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      followersCount: pkg.followersCount,
      platforms: pkg.platforms,
      price: pkg.price,
      originalPrice: pkg.originalPrice || pkg.price,
      description: pkg.description,
      features: pkg.features || [],
      deliveryTime: pkg.deliveryTime,
      guarantee: pkg.guarantee,
      isActive: pkg.isActive,
      isPopular: pkg.isPopular,
      discountPercentage: pkg.discountPercentage || 0,
      category: pkg.category,
      sortOrder: pkg.sortOrder
    });
    setShowCreateForm(true);
  };

  const handleDelete = (packageId) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      deletePackageMutation.mutate(packageId);
    }
  };

  const handlePlatformChange = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleFeatureChange = (index, value) => {
    setFormData(prev => {
      const newFeatures = [...prev.features];
      newFeatures[index] = value;
      return { ...prev, features: newFeatures };
    });
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-dark-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Manage Packages
            </h1>
            <p className="text-gray-600">
              Create and manage follower packages
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary"
          >
            Create New Package
          </button>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingPackage ? 'Edit Package' : 'Create New Package'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingPackage(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Package Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="input-field"
                      placeholder="e.g., Starter Pack"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Followers Count
                    </label>
                    <select
                      value={formData.followersCount}
                      onChange={(e) => setFormData({...formData, followersCount: parseInt(e.target.value)})}
                      className="input-field"
                      required
                    >
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                      <option value={750}>750</option>
                      <option value={1000}>1000</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platforms
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {['facebook', 'instagram', 'youtube', 'tiktok', 'twitter'].map(platform => (
                      <label key={platform} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.platforms.includes(platform)}
                          onChange={() => handlePlatformChange(platform)}
                          className="rounded border-gray-300 text-dark-purple-600 focus:ring-dark-purple-500"
                        />
                        <span className="ml-2 text-sm capitalize">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price ($)
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                      className="input-field"
                      placeholder="29.99"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Original Price ($)
                    </label>
                    <input
                      type="number"
                      value={formData.originalPrice}
                      onChange={(e) => setFormData({...formData, originalPrice: parseFloat(e.target.value)})}
                      className="input-field"
                      placeholder="39.99"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="input-field"
                    rows={3}
                    placeholder="Describe what this package includes..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features
                  </label>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureChange(index, e.target.value)}
                          className="input-field flex-1"
                          placeholder="e.g., Real Followers"
                        />
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-dark-purple-600 hover:text-dark-purple-700"
                    >
                      + Add Feature
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delivery Time
                    </label>
                    <input
                      type="text"
                      value={formData.deliveryTime}
                      onChange={(e) => setFormData({...formData, deliveryTime: e.target.value})}
                      className="input-field"
                      placeholder="1-3 days"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Guarantee
                    </label>
                    <input
                      type="text"
                      value={formData.guarantee}
                      onChange={(e) => setFormData({...formData, guarantee: e.target.value})}
                      className="input-field"
                      placeholder="30 days"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="input-field"
                    >
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-dark-purple-600 focus:ring-dark-purple-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isPopular}
                        onChange={(e) => setFormData({...formData, isPopular: e.target.checked})}
                        className="rounded border-gray-300 text-dark-purple-600 focus:ring-dark-purple-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Popular</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value)})}
                      className="input-field"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingPackage(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createPackageMutation.isLoading || updatePackageMutation.isLoading}
                    className="btn-primary"
                  >
                    {createPackageMutation.isLoading || updatePackageMutation.isLoading
                      ? 'Saving...'
                      : editingPackage ? 'Update Package' : 'Create Package'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Packages Table */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Followers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platforms
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {packages?.map((pkg) => (
                  <tr key={pkg._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pkg.name}
                        {pkg.isPopular && (
                          <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            Popular
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pkg.followersCount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {pkg.platforms?.map(platform => (
                          <span key={platform} className={`platform-badge platform-${platform}`}>
                            {platform.charAt(0).toUpperCase() + platform.slice(1)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${pkg.price}
                      {pkg.discountPercentage > 0 && (
                        <span className="ml-2 text-green-600 text-xs">
                          ({pkg.discountPercentage}% off)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pkg.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {pkg.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(pkg)}
                          className="text-dark-purple-600 hover:text-dark-purple-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(pkg._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {packages?.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No packages found
                </p>
                <p className="text-gray-400 mt-2">
                  Create your first package to get started
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackages;
